/**
 * The audit trail (KAN-46, `audit_log.view`).
 *
 * Read-only by construction, not by convention: the table rejects UPDATE and
 * DELETE at the database level and no write endpoint exists, so there is nothing
 * on this screen that could edit what it shows.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AUDITED_ENTITIES,
  type AuditAction,
  type AuditActorType,
  type AuditedEntity,
} from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { formatDateTime, shortId } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'

interface AuditEntry {
  id: string
  entityType: AuditedEntity
  entityId: string
  action: AuditAction
  actorType: AuditActorType
  actorUserId: string | null
  actorName: string | null
  actorEmail: string | null
  syncRunId: string | null
  changedFields: string[]
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  ipAddress: string | null
  occurredAt: string
}

interface AuditPage {
  meta: { total: number; perPage: number; currentPage: number; lastPage: number }
  entries: AuditEntry[]
}

interface Actor {
  id: string
  fullName: string
  email: string
}

const ACTIONS: AuditAction[] = ['create', 'update', 'delete']

const ACTION_TONES: Record<AuditAction, Tone> = {
  create: 'positive',
  update: 'brand',
  delete: 'critical',
}

/** Underscored table names are not what the business calls these things. */
const ENTITY_LABELS: Record<AuditedEntity, string> = {
  client: 'Client',
  attorney: 'Attorney',
  matter: 'Matter',
  matter_event: 'Matter calendar entry',
  matter_rate: 'Matter rate',
  time_entry: 'Time entry',
  invoice: 'Invoice',
  client_payment: 'Client payment',
  client_refund: 'Client refund',
  trust_ledger_entry: 'Trust ledger entry',
  vendor: 'Vendor',
  vendor_payment: 'Vendor payment',
  gl_account: 'Chart of accounts entry',
  budget: 'Budget',
  recurring_obligation: 'Recurring obligation',
  forecast_scenario: 'Forecast scenario',
  report_draft: 'Report draft',
  payment_commitment: 'Payment commitment',
  billing_rule_setting: 'Billing rule threshold',
  firm_goal_setting: 'Weekly retainer goal',
  billing_exception: 'Billing exception',
  /**
   * "Threshold" rather than "alert rule", because that is the word Finance uses
   * for it — and this log is read during review by somebody who will never see
   * the table name.
   */
  alert_rule: 'Alert threshold',
  alert: 'Exception alert',
  user: 'User account',
  integration_connection: 'Integration connection',
}

export function AuditLog() {
  const [entityType, setEntityType] = useState('')
  const [action, setAction] = useState('')
  const [actorUserId, setActorUserId] = useState('')
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<string | null>(null)

  const actors = useQuery({
    queryKey: ['audit-actors'],
    queryFn: () => apiFetch<{ actors: Actor[] }>('/audit-logs/actors'),
  })

  const query = new URLSearchParams({ page: String(page) })
  if (entityType) query.set('entityType', entityType)
  if (action) query.set('action', action)
  if (actorUserId) query.set('actorUserId', actorUserId)

  const log = useQuery({
    queryKey: ['audit-log', entityType, action, actorUserId, page],
    queryFn: () => apiFetch<AuditPage>(`/audit-logs?${query.toString()}`),
  })

  /** Any filter change invalidates the page number it was paired with. */
  function changeFilter(apply: () => void) {
    apply()
    setPage(1)
    setExpanded(null)
  }

  const entries = log.data?.entries ?? []
  const meta = log.data?.meta

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every create, update and delete on financial data, with the account or sync run responsible. Append-only — entries cannot be edited or removed, by anyone."
      />

      <Card className="mb-6">
        <CardBody>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field id="filter-entity" label="Record type">
              <Select
                id="filter-entity"
                value={entityType}
                onChange={(event) => changeFilter(() => setEntityType(event.target.value))}
              >
                <option value="">All types</option>
                {AUDITED_ENTITIES.map((entity) => (
                  <option key={entity} value={entity}>
                    {ENTITY_LABELS[entity]}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="filter-action" label="Action">
              <Select
                id="filter-action"
                value={action}
                onChange={(event) => changeFilter(() => setAction(event.target.value))}
              >
                <option value="">All actions</option>
                {ACTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              id="filter-actor"
              label="Actor"
              hint="Sync jobs are not listed here — filter by record type instead."
            >
              <Select
                id="filter-actor"
                value={actorUserId}
                disabled={actors.isPending}
                onChange={(event) => changeFilter(() => setActorUserId(event.target.value))}
              >
                <option value="">Anyone</option>
                {(actors.data?.actors ?? []).map((actor) => (
                  <option key={actor.id} value={actor.id}>
                    {actor.fullName}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Entries"
          description={
            log.isPending
              ? 'Loading…'
              : meta
                ? `${meta.total} matching ${meta.total === 1 ? 'entry' : 'entries'}`
                : undefined
          }
        />

        {log.error ? (
          <CardBody>
            <Alert tone="error">{messageFor(log.error)}</Alert>
          </CardBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th className="whitespace-nowrap">When</Th>
                <Th>Who</Th>
                <Th>Action</Th>
                <Th>Record</Th>
                <Th>Changed</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {log.isPending && <EmptyRow colSpan={6}>Loading entries…</EmptyRow>}

              {!log.isPending && entries.length === 0 && (
                <EmptyRow colSpan={6}>
                  Nothing recorded for these filters. The log fills as records are created and as
                  syncs write upstream data.
                </EmptyRow>
              )}

              {entries.map((entry) => (
                <Row
                  key={entry.id}
                  entry={entry}
                  expanded={expanded === entry.id}
                  onToggle={() => setExpanded(expanded === entry.id ? null : entry.id)}
                />
              ))}
            </tbody>
          </TableWrap>
        )}

        {meta && meta.lastPage > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-ink-100 px-5 py-3">
            <p className="text-sm text-ink-600">
              Page {meta.currentPage} of {meta.lastPage}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={meta.currentPage <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                disabled={meta.currentPage >= meta.lastPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  )
}

function Row({
  entry,
  expanded,
  onToggle,
}: {
  entry: AuditEntry
  expanded: boolean
  onToggle: () => void
}) {
  const fields = entry.changedFields ?? []

  return (
    <>
      <tr className={expanded ? 'bg-brand-50/50' : undefined}>
        <Td className="whitespace-nowrap text-ink-700">{formatDateTime(entry.occurredAt)}</Td>

        <Td>
          <Actor entry={entry} />
        </Td>

        <Td>
          <Badge tone={ACTION_TONES[entry.action]}>{entry.action}</Badge>
        </Td>

        <Td>
          <span className="block text-ink-900">{ENTITY_LABELS[entry.entityType]}</span>
          <code className="block font-mono text-[11px] text-ink-500">
            {shortId(entry.entityId)}
          </code>
        </Td>

        <Td>
          {fields.length === 0 ? (
            <span className="text-ink-500">—</span>
          ) : (
            <span className="text-ink-700">
              {fields.slice(0, 3).join(', ')}
              {fields.length > 3 && ` +${fields.length - 3} more`}
            </span>
          )}
        </Td>

        <Td className="text-right">
          <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={expanded}>
            {expanded ? 'Hide' : 'Detail'}
          </Button>
        </Td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} className="border-b border-ink-200 bg-ink-50 px-5 py-4">
            <Detail entry={entry} />
          </td>
        </tr>
      )}
    </>
  )
}

function Actor({ entry }: { entry: AuditEntry }) {
  if (entry.actorType === 'sync') {
    return (
      <>
        <span className="block text-ink-900">Sync job</span>
        <code className="block font-mono text-[11px] text-ink-500">
          run {shortId(entry.syncRunId)}
        </code>
      </>
    )
  }

  if (entry.actorType === 'system') {
    return <span className="text-ink-700">System</span>
  }

  /**
   * Three cases, and they mean different things. A named actor is the norm. An
   * id with no name is an account that has since been removed — the log holds
   * no foreign key to `users`, by design, so it outlives the account. No id at
   * all is a write that was never attributed; the table is append-only, so
   * historical rows in that state cannot be corrected and must be read as what
   * they are rather than blamed on a deletion.
   */
  if (!entry.actorUserId) {
    return <span className="text-ink-600">Not recorded</span>
  }

  return (
    <>
      <span className="block text-ink-900">{entry.actorName ?? 'Removed account'}</span>
      <span className="block text-xs text-ink-600">
        {entry.actorEmail ?? shortId(entry.actorUserId)}
      </span>
    </>
  )
}

function Detail({ entry }: { entry: AuditEntry }) {
  const fields = entry.changedFields?.length
    ? entry.changedFields
    : Object.keys(entry.after ?? entry.before ?? {})

  return (
    <div>
      <dl className="grid gap-x-6 gap-y-1 text-xs text-ink-600 sm:grid-cols-3">
        <div>
          <dt className="inline font-semibold">Record id: </dt>
          <dd className="inline font-mono">{entry.entityId}</dd>
        </div>
        <div>
          <dt className="inline font-semibold">Entry id: </dt>
          <dd className="inline font-mono">{entry.id}</dd>
        </div>
        <div>
          <dt className="inline font-semibold">From: </dt>
          <dd className="inline font-mono">{entry.ipAddress ?? 'not recorded'}</dd>
        </div>
      </dl>

      {fields.length === 0 ? (
        <p className="mt-3 text-sm text-ink-600">No field-level detail was recorded.</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-md border border-ink-200 bg-white">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr>
                <Th className="w-48">Field</Th>
                <Th>Before</Th>
                <Th>After</Th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field) => (
                <tr key={field}>
                  <Td className="font-mono text-ink-800">{field}</Td>
                  <Td className="font-mono break-all text-ink-600">
                    {display(entry.before?.[field])}
                  </Td>
                  <Td className="font-mono break-all text-ink-900">
                    {display(entry.after?.[field])}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-ink-600">
        Secret values — the OAuth tokens held for Clio and QuickBooks — are recorded as changed by
        name and shown as{' '}
        <code className="font-mono">[redacted]</code>. The value itself never enters the log.
      </p>
    </div>
  )
}

function display(value: unknown): string {
  if (value === undefined) return '—'
  if (value === null) return 'null'
  if (typeof value === 'string') return value === '' ? '""' : value
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function messageFor(error: unknown): string {
  return error instanceof ApiError
    ? `${error.message} (${error.status})`
    : 'Something went wrong. Try again.'
}
